import axiosInstance from '@/lib/axios';
import { nocoAxios } from '@/lib/noco.axios';
import { ENDPOINTS, NOCO } from './endpoints';
import type {
  ApiEmployee,
  ApiEmployeeCreatePayload,
  ApiEmployeeUpdatePayload,
  CustomField,
} from '@/types/employee.types';
import type { ApiResponse, TableQueryParams } from '@/types/common.types';

export interface TalentChekEmployeeResponse {
  employees: ApiEmployee[];
  pagination: {
    total: number;
  };
}

export const employeeApi = {
  // Using the new API endpoints for list, get, create, update, destroy
  getAll: async (params?: TableQueryParams) => {
    const pageSize = params?.limit || 200;
    let currentPage = 1;
    let allEmployees: ApiEmployee[] = [];
    let hasMore = true;

    // Recursively fetch all pages to ensure complete employee list
    while (hasMore) {
      const filterParams = {
        page: currentPage,
        pageSize,
        ...(params?.search ? { filter: JSON.stringify({ search: params.search }) } : {})
      };
      const response = await nocoAxios.get<ApiEmployee[]>(NOCO.EMPLOYEES.LIST, { params: filterParams });
      // Normalize response if it's an array directly or nested in data
      const data = Array.isArray(response.data) ? response.data : (response.data as unknown as { data?: ApiEmployee[] }).data || [];
      allEmployees = [...allEmployees, ...data];

      // If we got fewer results than pageSize, we've reached the last page
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      data: {
        employees: allEmployees,
        pagination: { total: allEmployees.length }
      }
    };
  },


  getByWorkEmail: async (email: string): Promise<ApiEmployee | null> => {
    const workEmail = email.trim().toLowerCase();
    if (!workEmail) throw new Error('Work email is required');
    try {
      const response = await nocoAxios.get<ApiEmployee | { data: ApiEmployee | null } | null>(
        NOCO.EMPLOYEES.GET,
        { params: { filter: JSON.stringify({ workEmail }) } }
      );
      const record = response.data;
      if (!record || Array.isArray(record)) return null;
      return 'data' in record ? record.data : record;
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
      throw error;
    }
  },

  getById: (id: string) =>
    nocoAxios.get<ApiEmployee>(NOCO.EMPLOYEES.GET, { params: { filterByTk: id } }),

  create: (payload: ApiEmployeeCreatePayload) =>
    nocoAxios.post<ApiEmployee>(NOCO.EMPLOYEES.CREATE, payload),

  update: (id: string, payload: ApiEmployeeUpdatePayload) => {
    return nocoAxios.post<ApiEmployee>(NOCO.EMPLOYEES.UPDATE, payload, { 
      params: { filterByTk: id } 
    });
  },

  delete: (id: string) =>
    nocoAxios.post<ApiResponse<null>>(NOCO.EMPLOYEES.DESTROY, {}, { 
      params: { filterByTk: id } 
    }),

  uploadAvatar: (id: string, file: File) => {
    const form = new FormData();
    form.append('avatar', file);
    return axiosInstance.post<ApiResponse<{ avatarUrl: string }>>(
      ENDPOINTS.EMPLOYEES.AVATAR(id),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  getCustomFields: () =>
    axiosInstance.get<ApiResponse<CustomField[]>>(ENDPOINTS.EMPLOYEES.CUSTOM_FIELDS),

  createCustomField: (payload: Omit<CustomField, 'id'>) =>
    axiosInstance.post<ApiResponse<CustomField>>(ENDPOINTS.EMPLOYEES.CUSTOM_FIELDS, payload),

  updateCustomField: (id: string, payload: Partial<CustomField>) =>
    axiosInstance.patch<ApiResponse<CustomField>>(`${ENDPOINTS.EMPLOYEES.CUSTOM_FIELDS}/${id}`, payload),

  deleteCustomField: (id: string) =>
    axiosInstance.delete<ApiResponse<null>>(`${ENDPOINTS.EMPLOYEES.CUSTOM_FIELDS}/${id}`),
};
